import type { JwtAccessClaims } from '../models/jwt-claims.model';

/**
 * Decodes a JWT payload without verifying the signature (UI / routing only).
 */
export function decodeJwtPayload<T extends object = JwtAccessClaims>(token: string): T | null {
  try {
    const parts = token.split('.');
    if (parts.length < 2 || !parts[1]) {
      return null;
    }
    let base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const pad = base64.length % 4;
    if (pad) {
      base64 += '='.repeat(4 - pad);
    }
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    const json = new TextDecoder().decode(bytes);
    return JSON.parse(json) as T;
  } catch {
    return null;
  }
}

/** Coerces JWT NumericDate claims (some stacks serialize them as strings). */
export function jwtNumericClaim(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string' && value.trim()) {
    const n = Number(value);
    if (Number.isFinite(n)) {
      return n;
    }
  }
  return null;
}

/** True when the string looks like a JWT (three base64url segments). */
export function looksLikeJwt(token: string): boolean {
  const parts = token.split('.');
  return parts.length === 3 && parts.every((p) => p.length > 0);
}

/**
 * True when the access JWT decodes and has a usable `exp` claim.
 * `jsonwebtoken` with `expiresIn` sets `exp` (and usually `iat`); we do not require both
 * on the client because switch-client also returns `expiresIn` separately.
 */
export function accessTokenHasLifetimeClaims(token: string): boolean {
  if (!looksLikeJwt(token)) {
    return false;
  }
  const claims = decodeJwtPayload<{ exp?: unknown; iat?: unknown }>(token);
  if (!claims) {
    return false;
  }
  const exp = jwtNumericClaim(claims.exp);
  return exp != null && exp > 0;
}

/** Seconds until JWT exp claim; negative if already expired. Null if no exp. */
export function secondsUntilJwtExpiry(token: string): number | null {
  const claims = decodeJwtPayload<{ exp?: unknown }>(token);
  const exp = jwtNumericClaim(claims?.exp);
  if (exp == null) {
    return null;
  }
  const now = Math.floor(Date.now() / 1000);
  return exp - now;
}
